
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { InductionSection } from "../Types";
import { ChatIcon, EditIcon, Tooltip } from "@chakra-ui/icons";

interface InductionQuickToolsProps {
  companyId?: string | number;
  employeeName?: string;
  companyName?: string;
  sections: InductionSection[];
  activeSection: InductionSection;
  onAskAssistant?: (
    question: string,
    context: {
      companyName?: string;
      employeeName?: string;
      sections: InductionSection[];
      activeSection: InductionSection;
    }
  ) => Promise<string>;
}

interface ChatMessage {
  id: string;
  role: "assistant" | "user";
  text: string;
}

function getContextualAnswer(
  question: string,
  sections: InductionSection[]
) {
  const terms = question
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word.length > 2);

  const matches = sections
    .map((item) => {
      const content = [
        item.title,
        item.label,
        item.intro,
        item.narration,
        ...(item.stats || []).flatMap((stat) => [
          stat.value,
          stat.label,
        ]),
        ...(item.items || []).flatMap((entry) => [
          entry.label,
          entry.text,
        ]),
      ]
        .filter(Boolean)
        .join(" ");

      const score = terms.reduce(
        (total, term) =>
          total + (content.toLowerCase().includes(term) ? 1 : 0),
        0
      );

      return { item, score };
    })
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  if (!matches.length) {
    return "I couldn't find that detail in the induction information currently available. Please ask your HR team for confirmation.";
  }

  return matches
    .slice(0, 2)
    .map(({ item }) => {
      const details = [
        item.intro,
        item.narration,
        ...(item.items || []).map(
          (entry) => `${entry.label}: ${entry.text}`
        ),
        ...(item.stats || []).map(
          (stat) => `${stat.label}: ${stat.value}`
        ),
      ].filter(Boolean);

      return `${item.title || item.label}: ${details
        .slice(0, 3)
        .join(" ")}`;
    })
    .join("\n\n");
}

export function InductionQuickTools({
  companyId,
  employeeName,
  companyName,
  sections,
  activeSection,
  onAskAssistant,
}: InductionQuickToolsProps) {
 const [isChatOpen, setIsChatOpen] = useState(false);
const [isNotesOpen, setIsNotesOpen] = useState(false);

  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);

  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: "welcome",
      role: "assistant",
      text: "Hello! I'm Sufi. Ask me a question about the information in your induction.",
    },
  ]);

  const messagesContainerRef = useRef<HTMLDivElement | null>(null);

  const storageKey = `induction-notes-${companyId ?? "company"}-${employeeName ?? "employee"}`;

  const [notes, setNotes] = useState(() => {
    if (typeof window === "undefined") return "";

    try {
      return window.localStorage.getItem(storageKey) || "";
    } catch {
      return "";
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey, notes);
    } catch (error) {
      console.warn("Unable to save induction notes.", error);
    }
  }, [notes, storageKey]);

  // Scroll only the chat message container.
 // Scroll to the latest message whenever the chat opens
// or a new message/loading state is added.
useLayoutEffect(() => {
  if (!isChatOpen) return;

  const container = messagesContainerRef.current;

  if (container) {
    container.scrollTop = container.scrollHeight;
  }
}, [isChatOpen]);
useEffect(() => {
  if (!isChatOpen) return;

  const container = messagesContainerRef.current;

  if (container) {
    container.scrollTo({
      top: container.scrollHeight,
      behavior: "smooth",
    });
  }
}, [messages, chatLoading, isChatOpen]);

  const sendMessage = useCallback(async () => {
    const question = chatInput.trim();

    if (!question || chatLoading) return;

    setMessages((current) => [
      ...current,
      {
        id: `user-${Date.now()}`,
        role: "user",
        text: question,
      },
    ]);

    setChatInput("");
    setChatLoading(true);

    try {
      const answerPromise = onAskAssistant
        ? onAskAssistant(question, {
            companyName,
            employeeName,
            sections,
            activeSection,
          })
        : Promise.resolve(
            getContextualAnswer(question, sections)
          );

      const minimumDelay = new Promise((resolve) =>
        setTimeout(resolve, 1200)
      );

      const [answer] = await Promise.all([
        answerPromise,
        minimumDelay,
      ]);

      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          text: answer,
        },
      ]);
    } catch (error) {
      console.error("Sufi chat request failed:", error);

      setMessages((current) => [
        ...current,
        {
          id: `assistant-error-${Date.now()}`,
          role: "assistant",
          text: "I'm unable to answer right now. Please try again in a moment.",
        },
      ]);
    } finally {
      setChatLoading(false);
    }
  }, [
    chatInput,
    chatLoading,
    onAskAssistant,
    companyName,
    employeeName,
    sections,
    activeSection,
  ]);

 return (
  <div className="induction-tools">

    {/* BOTH PANELS IN ONE FLEX ROW */}
    <div className="induction-tool-panels" >

      {/* CHAT PANEL */}
      {isChatOpen && (
        <section
          className="induction-tool-panel induction-chat-panel"
          aria-label="Chat with Sufi"
        >
               <header className="induction-tool-header">
            <div>
              <strong>Ask Sufi</strong>
              <span>INDUCTION ASSISTANT</span>
            </div>

            <button
              type="button"
              aria-label="Close chat"
              onClick={() => setIsChatOpen(false)}
            >
              ×
            </button>
          </header>

          <div
            ref={messagesContainerRef}
            className="induction-chat-messages"
            aria-live="polite"
          >
            {messages.map((message) => (
              <div
                key={message.id}
                className={`induction-chat-message is-${message.role}`}
              >
                {message.text}
              </div>
            ))}

            {chatLoading && (
              <div className="induction-chat-message is-assistant is-typing">
                Sufi is typing…
              </div>
            )}
          </div>

          <form
            className="induction-chat-form"
            onSubmit={(event) => {
              event.preventDefault();
              void sendMessage();
            }}
          >
            <input
              value={chatInput}
              onChange={(event) =>
                setChatInput(event.target.value)
              }
              placeholder="Ask about your induction..."
              aria-label="Ask Sufi a question"
              disabled={chatLoading}
            />

            <button
              type="submit"
              disabled={!chatInput.trim() || chatLoading}
              aria-label="Send question"
            >
              ➤
            </button>
          </form>

          {!onAskAssistant && (
            <p className="induction-chat-disclaimer">
              Answers are based on the induction information loaded on this page.
            </p>
          )}
        </section>
      )}

      {/* NOTEPAD PANEL */}
      {isNotesOpen && (
        <section
          className="induction-tool-panel induction-notes-panel"
          aria-label="Induction notepad"
        >
           <header className="induction-tool-header">
            <div>
              <strong>My Notes</strong>
              <span>PRIVATE INDUCTION NOTES</span>
            </div>

            <button
              type="button"
              aria-label="Close notepad"
              onClick={() => setIsNotesOpen(false)}
            >
              ×
            </button>
          </header>

          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Write down important points, questions, or reminders..."
            aria-label="Your induction notes"
          />

          <footer className="induction-notes-footer">
            <span>Saved automatically on this device</span>

            <button
              type="button"
              onClick={() => setNotes("")}
            >
              Clear notes
            </button>
          </footer>
        </section>
      )}

    </div>

    {/* FLOATING BUTTONS */}
    <div className="induction-floating-actions">

      <Tooltip label="Chat with Sufi" placement="left" hasArrow>
        <button
          type="button"
          className={`induction-tools-launcher induction-chat-launcher ${
            isChatOpen ? "is-active" : ""
          }`}
          aria-label="Chat with Sufi"
          aria-expanded={isChatOpen}
          onClick={() => setIsChatOpen((prev) => !prev)}
        >
          <ChatIcon boxSize={5} />
        </button>
      </Tooltip>

      <Tooltip label="My Notes" placement="left" hasArrow>
        <button
          type="button"
          className={`induction-tools-launcher induction-notes-launcher ${
            isNotesOpen ? "is-active" : ""
          }`}
          aria-label="Open Notepad"
          aria-expanded={isNotesOpen}
          onClick={() => setIsNotesOpen((prev) => !prev)}
        >
          <EditIcon boxSize={5} />
        </button>
      </Tooltip>

    </div>
  </div>
);
}
